<template>
  <v-dialog v-model="dialog" max-width="980" scrollable>
    <v-card class="logs-card">
      <div class="logs-page">
        <div class="logs-top">
          <div class="d-flex align-start justify-space-between" style="gap: 12px">
            <div class="flex-grow-1 min-w-0">
              <div class="text-h6 font-weight-bold mb-2">{{ caseTitle }}</div>
              <div class="text-body-2 text-medium-emphasis">
                Case ID: {{ caseId }}
                <span class="meta-dot">•</span>
                {{ logCount }} {{ logCount === 1 ? "log" : "logs" }}
              </div>
            </div>
            <v-btn
              variant="text"
              color="primary"
              prepend-icon="mdi-arrow-left"
              class="text-none flex-shrink-0"
              @click="handleClose"
            >
              Back
            </v-btn>
          </div>
        </div>

        <div class="chat-logs-header">
          <div class="text-subtitle-1 font-weight-bold">Chat logs</div>
          <div class="text-body-2 text-medium-emphasis">
            AI conversation that created this case
          </div>
        </div>

        <div class="logs-scroll">
          <div v-if="conversation.length" class="timeline">
            <div
              v-for="(msg, index) in conversation"
              :key="msg.id"
              class="timeline-item"
              :class="{ 'is-last': index === conversation.length - 1 }"
            >
              <div class="timeline-rail">
                <v-avatar
                  :color="msg.role === 'bot' ? 'orange-darken-1' : 'grey'"
                  size="36"
                  class="timeline-avatar"
                >
                  <v-icon v-if="msg.role === 'bot'" size="18" color="white">
                    mdi-robot-outline
                  </v-icon>
                  <span v-else class="text-white text-caption font-weight-bold">
                    {{ userInitial }}
                  </span>
                </v-avatar>
                <div v-if="index !== conversation.length - 1" class="timeline-line"></div>
              </div>

              <div class="timeline-content">
                <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
                  <span class="font-weight-bold">{{ msg.sender }}</span>
                  <v-chip
                    v-if="msg.role === 'bot'"
                    size="x-small"
                    color="warning"
                    variant="tonal"
                  >
                    AI
                  </v-chip>
                  <span class="text-caption text-medium-emphasis">• {{ msg.time }}</span>
                </div>

                <div
                  v-if="msg.role === 'bot'"
                  class="bot-bubble"
                  v-html="formatBotHtml(msg.text)"
                ></div>
                <div v-else class="user-text">{{ msg.text }}</div>
              </div>
            </div>
          </div>

          <div v-else class="empty-logs">
            <v-icon size="40" color="grey-lighten-1">mdi-chat-remove-outline</v-icon>
            <div class="text-body-2 text-medium-emphasis mt-2">
              No chatbot conversation is available for this case.
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatDate, normalizeChatbotLogs } from "@/mixins/RCSSupport/TicketApi";

export default {
  name: "ChatbotLogsDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    ticket: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    caseTitle() {
      return this.ticket?.subject || this.ticket?.title || "Chat logs";
    },
    caseId() {
      return this.ticket?.caseId || this.ticket?.ticketId || "-";
    },
    userName() {
      const name =
        this.ticket?.reporter_name ||
        this.ticket?.reporterName ||
        this.ticket?.reporter ||
        this.ticket?.reporter_email ||
        "User";
      return String(name).trim() || "User";
    },
    userInitial() {
      return this.userName.charAt(0).toUpperCase();
    },
    conversation() {
      const fromLogs = this.messagesFromLogs();
      if (fromLogs.length) return fromLogs;
      return this.messagesFromSummary();
    },
    logCount() {
      return this.conversation.length;
    },
  },
  methods: {
    handleClose() {
      this.dialog = false;
    },
    formatLogTime(value) {
      if (!value) return formatDate(value);
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return formatDate(value);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    messagesFromLogs() {
      const logs = normalizeChatbotLogs(
        this.ticket?.chatbotLogs || this.ticket?.chatbot_logs
      )
        .slice()
        .sort((a, b) => Number(a.created_on || 0) - Number(b.created_on || 0));

      const messages = [];
      logs.forEach((log, index) => {
        const time = this.formatLogTime(log.created_on);
        const logId = log.log_id || index;
        const query = String(log.query || "").trim();
        const response = String(log.response || "").trim();

        if (query) {
          messages.push({
            id: `${logId}-query`,
            role: "user",
            sender: this.userName,
            text: query,
            time,
          });
        }
        if (response) {
          messages.push({
            id: `${logId}-response`,
            role: "bot",
            sender: "RCS Support Bot",
            text: response,
            time,
          });
        }
      });
      return messages;
    },
    messagesFromSummary() {
      const summary = String(
        this.ticket?.aiOriginSummary || this.ticket?.ai_origin_summary || ""
      ).trim();
      if (!summary) return [];

      const chunks = summary.split(/\n(?=(?:Assistant|User)\s*:)/i);
      return chunks
        .map((chunk, index) => {
          const match = chunk.match(/^(Assistant|User)\s*:\s*([\s\S]*)$/i);
          if (!match) return null;
          const role = match[1].toLowerCase() === "assistant" ? "bot" : "user";
          const text = String(match[2] || "").trim();
          if (!text) return null;
          return {
            id: `summary-${index}`,
            role,
            sender: role === "bot" ? "RCS Support Bot" : this.userName,
            text,
            time: this.formatLogTime(
              this.ticket?.created_on || this.ticket?.createdOn
            ),
          };
        })
        .filter(Boolean);
    },
    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    formatBotHtml(text) {
      let html = this.escapeHtml(text);
      html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(
        /(\+\s*ADD(?:\s+[A-Z][A-Za-z]+)*)/g,
        "<strong>$1</strong>"
      );
      html = html.replace(
        /\b((?:My|Add|Edit|Delete|Create)\s+[A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+)*)\b/g,
        "<strong>$1</strong>"
      );
      html = html.replace(/\b(Residents|Recommendations)\b/g, "<strong>$1</strong>");
      html = html.replace(/\n/g, "<br>");
      return html;
    },
  },
};
</script>

<style scoped>
.logs-card {
  overflow: hidden;
}

.logs-page {
  display: flex;
  flex-direction: column;
  height: 82vh;
  max-height: 82vh;
  overflow: hidden;
  background: #fff;
  padding: 20px 24px 16px;
}

.min-w-0 {
  min-width: 0;
}

.logs-top {
  flex-shrink: 0;
  padding-bottom: 16px;
}

.meta-dot {
  color: rgba(0, 0, 0, 0.3);
  margin: 0 6px;
}

.chat-logs-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0 16px;
}

.logs-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}

.timeline {
  display: flex;
  flex-direction: column;
}

.timeline-item {
  display: flex;
  gap: 14px;
}

.timeline-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 36px;
  flex-shrink: 0;
}

.timeline-avatar {
  z-index: 1;
}

.timeline-line {
  width: 2px;
  flex: 1;
  min-height: 24px;
  background: #e6e8ee;
  margin-top: 8px;
}

.timeline-content {
  flex: 1;
  min-width: 0;
  padding-bottom: 28px;
}

.timeline-item.is-last .timeline-content {
  padding-bottom: 8px;
}

.user-text {
  font-size: 14px;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.82);
  white-space: pre-wrap;
  word-break: break-word;
}

.bot-bubble {
  background: #fff7f0;
  border: 1px solid #fdecd8;
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.65;
  color: rgba(0, 0, 0, 0.8);
  white-space: normal;
  word-break: break-word;
}

.bot-bubble :deep(strong) {
  font-weight: 700;
}

.empty-logs {
  text-align: center;
  padding: 56px 20px;
}
</style>
